import { defineStore } from "pinia";
import {
  PricesStore,
  Price,
  FilterPriceCondition,
  PriceSearchPagination,
  PaginatedPricesResponse,
} from "@/catalog/types/prices";
import type { UpdatePriceRequest, AdjustInventoryRequest } from "@nectarpay/api-contracts";
import type {
  CatalogProductFilters,
  PriceExportRequest,
  PriceExportScope,
} from "@nectarpay/api-contracts";
import type { AdjustInventoryResponse } from "@/catalog/types/products";
import type { TaxBehavior } from "@/shared/enums/taxes.enum";
import axios from "@/app/plugins/axios";
import { useSnackBarStore } from "@/shared/stores/snackbar";
import { formatMoney } from "@/shared/services/util.service";
import { db } from "@/app/plugins/firebase";
import { doc, onSnapshot } from "firebase/firestore";
import { useAccountStore } from "@/account/stores/account";
import { useLocationStore } from "@/terminals/stores/locations";
import { getErrorMessage } from "@/shared/utils/errorMessage";
import i18n from "@/app/plugins/i18n";
import type { ProductFilterCondition } from "@/catalog/types/products";

export interface CreatePricePayload {
  product: string;
  nickname?: string;
  active?: boolean;
  isDefault?: boolean;
  locationIds?: string[];
  unit_amount: number;
  hasInventory?: boolean;
  sku?: string;
  lowStockThreshold?: number | null;
  stockCount?: number;
  tax_behavior?: TaxBehavior | "inclusive" | "exclusive" | "unspecified";
}

export interface FetchPricesOptions {
  params?: { limit?: number; page?: number; [key: string]: string | number | boolean | undefined };
}

type PriceSearchFilters = Partial<FilterPriceCondition>;

export const usePricesStore = defineStore("price", {
  state: (): PricesStore => ({
    prices: [],
    selectedProductPrice: null,
    loading: false,
    filterPriceCondition: {
      nickname: "",
      active: undefined,
      lowStock: false,
      outOfStock: false,
      stockMin: undefined,
      stockMax: undefined,
      product: undefined,
      hasInventory: undefined,
    },
    listLoading: false,
    noResults: false,
    hasMore: false,
    hasNext: false,
    hasPrev: false,
    nextPage: "",
    previousPage: "",
    isChecked: false,
    selectedDefaultPrice: null,
    priceListeners: new Map(),
    exporting: false,
    exportError: null,
  }),

  getters: {
    isLoading: (state) => state.loading,
    isListLoading: (state) => state.listLoading,
    getPrices: (state) => state.prices,
    getSelectedProductPrice: (state) => state.selectedProductPrice,
    isExporting: (state) => state.exporting,
  },

  actions: {
    formatUnitAmount() {
      formatMoney({
        hideCurrency: true,
        amount: this.selectedProductPrice?.unit_amount,
      });
    },
    addPrice(newPrice: Price) {
      this.prices.push(newPrice);
    },
    setFilterPriceCondition(newCondition: Partial<FilterPriceCondition>) {
      // Create a copy of the new condition to avoid modifying the input directly
      const updatedCondition = { ...newCondition };

      // If outOfStock is being set to true, ensure lowStock is set to false
      if (updatedCondition.outOfStock === true) {
        updatedCondition.lowStock = false;
      }

      // If lowStock is being set to true, ensure outOfStock is set to false
      if (updatedCondition.lowStock === true) {
        updatedCondition.outOfStock = false;
      }

      // If sku is being set, ensure nickname is set to empty string
      if (updatedCondition.sku) {
        updatedCondition.nickname = undefined;
      }

      // If nickname is being set, ensure sku is set to empty string
      if (updatedCondition.nickname) {
        updatedCondition.sku = undefined;
      }

      // Apply the updated condition to the filter
      this.filterPriceCondition = { ...this.filterPriceCondition, ...updatedCondition };
    },
    setSelectedProductPrice(price: Price | null): void {
      this.selectedProductPrice = price;
    },
    setProductPrices(prices: Price[]): void {
      this.prices = prices;
    },

    // Helper function to build filters based on current filter conditions
    buildFiltersFromCondition(): PriceSearchFilters {
      const filters: PriceSearchFilters = {};

      if (this.filterPriceCondition.product) {
        filters.product = this.filterPriceCondition.product;
      }

      // Add support for filtering by specific price ID
      if (this.filterPriceCondition.priceId) {
        filters.priceId = this.filterPriceCondition.priceId;
      }

      if (this.filterPriceCondition.nickname) {
        filters.nickname = this.filterPriceCondition.nickname;
      } else if (this.filterPriceCondition.sku) {
        filters.sku = this.filterPriceCondition.sku;
      }

      if (this.filterPriceCondition.active === true || this.filterPriceCondition.active === false) {
        filters.active = this.filterPriceCondition.active;
      }

      // Include stock filter parameters
      if (this.filterPriceCondition.lowStock) {
        filters.lowStock = true;
      }

      if (this.filterPriceCondition.outOfStock) {
        filters.outOfStock = true;
        // When outOfStock is true, don't apply stockMin and stockMax filters
      } else {
        // Only apply stock range filters if not looking for out-of-stock items
        if (this.filterPriceCondition.stockMin !== undefined) {
          filters.stockMin = this.filterPriceCondition.stockMin;
        }

        if (this.filterPriceCondition.stockMax !== undefined) {
          filters.stockMax = this.filterPriceCondition.stockMax;
        }
      }

      if (this.filterPriceCondition.hasInventory) {
        filters.hasInventory = true;
      }

      return filters;
    },

    async updatePrice(
      updateData: UpdatePriceRequest,
      id: string | string[] | number,
    ): Promise<Price | undefined> {
      try {
        const response = await axios.patch<Price>(`/price/${id}`, updateData);
        const priceData = response.data;
        if (this.isChecked) {
          this.selectedProductPrice = priceData;
        }
        const index = this.prices.findIndex((price: Price) => price.id === id);
        if (index !== -1) {
          this.prices[index] = response.data;
        }
        return response.data;
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
      }
    },
    async createPrice({
      product,
      nickname,
      active,
      isDefault,
      locationIds,
      unit_amount,
      hasInventory,
      sku,
      lowStockThreshold,
      stockCount,
      tax_behavior,
    }: CreatePricePayload) {
      try {
        const payload = {
          product,
          nickname,
          active,
          isDefault,
          locationIds,
          unit_amount,
          hasInventory,
          sku,
          lowStockThreshold,
          stockCount,
          tax_behavior,
        };

        const response = await axios.post<Price>("/price", payload);
        if (payload.isDefault) {
          this.selectedDefaultPrice = response?.data ?? null;
        }
        this.selectedProductPrice = response?.data;

        const data = response?.data;

        return { data };
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
      }
    },

    async fetchPrices(apiOptions?: FetchPricesOptions): Promise<void> {
      this.listLoading = true;
      try {
        const response = await axios.get<PaginatedPricesResponse>("/price/search", apiOptions);
        const pricesFetched = response.data.data;
        this.hasMore = response.data.has_more ?? false;
        this.noResults = pricesFetched.length === 0;
        this.prices = [...pricesFetched];
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
      } finally {
        this.listLoading = false;
      }
    },

    async searchPrices(
      { pagination = {} }: { pagination?: PriceSearchPagination } = {},
      initLoading = false,
    ) {
      try {
        if (initLoading) this.loading = true;
        else this.listLoading = true;

        this.noResults = false;

        // If filters are not provided then don't call backend.
        if (Object.keys(this.filterPriceCondition).length === 0) {
          return;
        }

        // Use the abstracted filter building function
        const filters = this.buildFiltersFromCondition();

        const payload = {
          filters,
          pagination: {
            ...pagination,
            limit: 10, //default
          },
        };

        const response = await axios.post<PaginatedPricesResponse>("/price/search", payload);
        const filteredPrices = response.data?.data;
        this.hasPrev = response.data.hasPrev ?? false;
        this.hasNext = response.data.hasNext ?? false;
        this.listLoading = false;
        this.noResults = filteredPrices.length === 0;
        this.prices = [...filteredPrices];

        return this.prices;
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
        this.listLoading = false;
        this.loading = false;
      } finally {
        this.listLoading = false;
        this.loading = false;
      }
    },

    async exportPrices(
      productFilters: ProductFilterCondition = {},
      priceScope: PriceExportScope = "all_active",
    ): Promise<boolean> {
      this.exporting = true;
      this.exportError = null;

      try {
        const account = useAccountStore().account;
        const locationBasedInventoryEnabled =
          account?.accountSettings?.inventory?.location_based_inventory_enabled === true;
        const resolvedFilters: ProductFilterCondition = { ...productFilters };

        if (!locationBasedInventoryEnabled) {
          delete resolvedFilters.locationId;
        } else {
          const hasLocationId = Object.prototype.hasOwnProperty.call(resolvedFilters, "locationId");
          const locationId = resolvedFilters.locationId;
          const hasExplicitLocationFilter =
            (hasLocationId && locationId === null) ||
            (typeof locationId === "string" && Boolean(locationId));

          if (!hasExplicitLocationFilter) {
            const selectedLocationId = useLocationStore().selectedLocation?.id;
            if (selectedLocationId) {
              resolvedFilters.locationId = selectedLocationId;
            }
          }
        }

        useSnackBarStore().toastInfo({
          message: i18n.global.t("common.action.preparingExport"),
        });

        const payload: PriceExportRequest = {
          filters: resolvedFilters as CatalogProductFilters,
          priceScope,
        };
        const response = await axios.post("/price/export", payload, {
          responseType: "blob",
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        const contentDisposition = response.headers["content-disposition"];
        const filenameMatch = contentDisposition?.match(/filename="?([^";]+)"?/i);
        link.download = filenameMatch?.[1] || "prices_export.csv";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        useSnackBarStore().toastSuccess({
          message: i18n.global.t("common.action.exportPricesSuccess"),
        });
        return true;
      } catch (error) {
        const message = getErrorMessage(error, i18n.global.t("common.action.exportPricesError"));
        this.exportError = message;
        useSnackBarStore().toastError({ message });
        return false;
      } finally {
        this.exporting = false;
      }
    },

    async retrievePrice(priceId: string, _expandProduct: boolean) {
      try {
        const response = await axios.get<Price>(`/price/${priceId}`);
        const data = response.data;
        this.selectedProductPrice = data;

        return data;
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
      }
    },

    async listPrices(productId: string) {
      try {
        const payload = {
          params: { product: productId, limit: 10 },
        };

        const response = await axios.get<PaginatedPricesResponse>(`/price`, payload);

        const data = response.data.data;

        return data;
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
      }
    },

    async listAllPrices() {
      try {
        const response = await axios.get<PaginatedPricesResponse>(`/price`);
        const data = response.data.data;
        return data;
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
      }
    },

    async adjustInventory(
      priceId: string,
      adjustmentData: AdjustInventoryRequest,
    ): Promise<AdjustInventoryResponse | undefined> {
      try {
        this.listLoading = true;
        const response = await axios.post<AdjustInventoryResponse>(
          `/price/${priceId}/adjust-inventory`,
          adjustmentData,
        );

        // Update the price in the local store if it exists in the current list
        const index = this.prices.findIndex((price: Price) => price.id === priceId);
        if (index !== -1 && response.data) {
          // Update the stock count in the price object
          this.prices[index].stockCount = response.data.stockCount;
        }

        // If this is the currently selected price, update it too
        if (this.selectedProductPrice && this.selectedProductPrice.id === priceId) {
          this.selectedProductPrice.stockCount = response.data.stockCount;
        }

        return response.data;
      } catch (error) {
        useSnackBarStore().toastError({
          message: getErrorMessage(error, "Failed to adjust inventory"),
        });
        throw error;
      } finally {
        this.listLoading = false;
      }
    },

    // Setup a real-time listener for a specific price
    setupPriceListener(priceId: string): void {
      // Don't setup duplicate listeners
      if (this.priceListeners.has(priceId)) {
        return;
      }

      const accountId = useAccountStore().getAccountId;
      if (!accountId) {
        console.warn("Account ID not available, cannot setup price listener");
        return;
      }

      // Reference to the specific price document
      const priceRef = doc(db, "accounts", accountId, "prices", priceId);

      // Set up the real-time listener
      const unsubscribe = onSnapshot(
        priceRef,
        (snapshot) => {
          if (snapshot.exists()) {
            const updatedPrice = snapshot.data() as Partial<Price>;

            // Find and update the price in the prices array
            const index = this.prices.findIndex((price) => price.id === priceId);
            if (index !== -1) {
              // Merge the updated data with the existing price object
              this.prices[index] = {
                ...this.prices[index],
                ...updatedPrice,
                // Ensure these specific fields are updated
                stockCount: updatedPrice.stockCount ?? this.prices[index].stockCount,
                checkoutCount: updatedPrice.checkoutCount ?? this.prices[index].checkoutCount,
                lowStockThreshold:
                  updatedPrice.lowStockThreshold ?? this.prices[index].lowStockThreshold,
              };
            }
          }
        },
        (error) => {
          console.error(`Error listening to price ${priceId}:`, error);
        },
      );

      // Store the unsubscribe function
      this.priceListeners.set(priceId, unsubscribe);
    },

    // Remove a listener for a specific price
    removePriceListener(priceId: string): void {
      const unsubscribe = this.priceListeners.get(priceId);
      if (unsubscribe) {
        unsubscribe();
        this.priceListeners.delete(priceId);
      }
    },

    // Setup listeners for all current prices
    setupAllPriceListeners(): void {
      // First, remove any existing listeners
      this.removeAllPriceListeners();

      // Then setup listeners for all current prices
      this.prices.forEach((price) => {
        this.setupPriceListener(price.id);
      });
    },

    // Remove all price listeners
    removeAllPriceListeners(): void {
      this.priceListeners.forEach((unsubscribe) => {
        unsubscribe();
      });
      this.priceListeners.clear();
    },

    resetFilterPriceCondition() {
      this.setFilterPriceCondition({
        nickname: "",
        active: undefined,
        lowStock: false,
        outOfStock: false,
        stockMin: undefined,
        stockMax: undefined,
        product: undefined,
        hasInventory: undefined,
        sku: undefined,
      });
    },

    // Reset all filters and perform an initial search
    async resetFiltersAndSearch(paginationLimit = 10): Promise<void> {
      // Clear the prices array to remove old data
      this.prices = [];

      // Reset filter conditions to ensure a fresh search
      this.resetFilterPriceCondition();

      // Perform initial search with clean filters
      await this.searchPrices(
        {
          pagination: { limit: paginationLimit },
        },
        true,
      );
    },
  },
});
